import { useEffect, useState } from "react";
import api, { inr, formatApiErrorDetail, imgUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ImageUploader } from "@/components/ImageUploader";
import { toast } from "sonner";
import { Plus, Crown, Pencil } from "lucide-react";

const EMPTY = { type: "bike", name: "", description: "", city: "", price_from: 0, contact_phone: "", contact_whatsapp: "", images: [] };

export default function ProviderDashboard() {
  const [services, setServices] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [show, setShow] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const [s, i] = await Promise.all([
      api.get("/provider/services").then((r) => r.data),
      api.get("/provider/inquiries").then((r) => r.data),
    ]);
    setServices(s); setInquiries(i);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startEdit = (s) => {
    setForm({
      type: s.type, name: s.name, description: s.description || "", city: s.city,
      price_from: s.price_from, contact_phone: s.contact_phone || "",
      contact_whatsapp: s.contact_whatsapp || "", images: s.images || [],
    });
    setEditingId(s.id);
    setShow(true);
  };

  const save = async () => {
    try {
      const payload = { ...form, price_from: Number(form.price_from), images: form.images.filter(Boolean) };
      if (editingId) {
        await api.put(`/provider/services/${editingId}`, payload);
        toast.success("Service updated!");
      } else {
        await api.post("/services", payload);
        toast.success("Service listed — free for 30 days!");
      }
      setShow(false); setForm(EMPTY); setEditingId(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const activate = async (id) => {
    try { await api.post(`/provider/services/${id}/subscription/activate`); toast.success("Subscription activated ₹199/mo"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="bg-[#F7F7F9] min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div><h1 className="text-3xl font-display font-bold">Service provider dashboard</h1><p className="text-[#717171]">₹199/mo per listing · first month free</p></div>
          <Button data-testid="add-service-btn" onClick={() => { setForm(EMPTY); setEditingId(null); setShow((v) => !v); }} className="rounded-xl bg-[#222] hover:bg-black text-white"><Plus className="w-4 h-4 mr-1" />Add service</Button>
        </div>

        {show && (
          <div className="bg-white border border-[#EBEBEB] rounded-2xl p-6 mb-8 grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Type</Label><select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full border border-[#DDD] rounded-xl px-3 py-2"><option>bike</option><option>car</option><option>taxi</option><option>yacht</option></select></div>
            <div><Label className="text-xs">Name</Label><Input data-testid="service-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl" /></div>
            <div><Label className="text-xs">City</Label><Input data-testid="service-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-xl" /></div>
            <div><Label className="text-xs">Starting price (INR)</Label><Input type="number" value={form.price_from} onChange={(e) => setForm({ ...form, price_from: e.target.value })} className="rounded-xl" /></div>
            <div><Label className="text-xs">Phone</Label><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="+91..." className="rounded-xl" /></div>
            <div><Label className="text-xs">WhatsApp</Label><Input value={form.contact_whatsapp} onChange={(e) => setForm({ ...form, contact_whatsapp: e.target.value })} placeholder="+91..." className="rounded-xl" /></div>
            <div className="sm:col-span-2"><Label className="text-xs">Photos</Label><ImageUploader testId="service-images-uploader" value={form.images} onChange={(imgs) => setForm({ ...form, images: imgs })} multiple /></div>
            <div className="sm:col-span-2"><Label className="text-xs">Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl" /></div>
            <Button data-testid="save-service-btn" onClick={save} className="rounded-xl bg-[#FF385C] hover:bg-[#D90B38] text-white sm:col-span-2">{editingId ? "Save changes" : "Create listing"}</Button>
          </div>
        )}

        <div className="bg-white border border-[#EBEBEB] rounded-2xl mb-8">
          <h2 className="font-display font-semibold p-5 border-b border-[#EBEBEB]">My listings</h2>
          {services.length === 0 ? <p className="p-6 text-[#717171] text-sm">None yet.</p> : (
            <div className="divide-y divide-[#EBEBEB]">
              {services.map((s) => (
                <div key={s.id} className="p-4 flex items-center justify-between">
                  <div><p className="font-semibold">{s.name} <span className="text-xs text-[#717171] uppercase">· {s.type}</span></p><p className="text-xs text-[#717171]">{s.city} · from {inr(s.price_from)} · sub: {s.subscription?.status}</p></div>
                  <div className="flex items-center gap-2">
                    <Button data-testid={`edit-svc-${s.id}`} variant="outline" onClick={() => startEdit(s)} className="rounded-xl text-xs h-8"><Pencil className="w-3 h-3 mr-1" />Edit</Button>
                    {s.subscription?.status !== "active" && <Button data-testid={`activate-svc-${s.id}`} onClick={() => activate(s.id)} className="rounded-xl bg-[#FF385C] hover:bg-[#D90B38] text-white text-xs h-8"><Crown className="w-3 h-3 mr-1" />Activate ₹199/mo</Button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-[#EBEBEB] rounded-2xl">
          <h2 className="font-display font-semibold p-5 border-b border-[#EBEBEB]">Inquiries with contact</h2>
          {inquiries.length === 0 ? <p className="p-6 text-[#717171] text-sm">No inquiries yet.</p> : (
            <div className="divide-y divide-[#EBEBEB]">
              {inquiries.map((i) => (
                <div key={i.id} className="p-4 text-sm"><p><strong>{i.name}</strong> · {i.phone} · {i.email} → {i.service_name}</p><p className="text-xs text-[#717171] mt-1">{i.when} — "{i.message}"</p></div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
